import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Datepicker, Icon, Text } from '@/components'

/** Parse a hand-typed date: 4/23/2026, 04/23/26, 2026-04-23, "Apr 23 2026".
 *  Returns YYYY-MM-DD or null when unparseable. */
function parseTypedDate(text: string): string | null {
  const t = text.trim()
  if (!t) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const d = dayjs(t)
    return d.isValid() ? d.format('YYYY-MM-DD') : null
  }
  const slash = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (slash) {
    const year = slash[3].length === 2 ? `20${slash[3]}` : slash[3]
    const d = dayjs(
      `${year}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`,
    )
    return d.isValid() ? d.format('YYYY-MM-DD') : null
  }
  const native = new Date(t)
  return isNaN(native.getTime()) ? null : dayjs(native).format('YYYY-MM-DD')
}

export interface DateRangePickerProps {
  /** ISO date string (YYYY-MM-DD) or null when unset */
  startDate: string | null
  /** ISO date string (YYYY-MM-DD) or null when unset */
  endDate: string | null
  onChange: (startDate: string | null, endDate: string | null) => void
  disabled?: boolean
  id?: string
}

type RangeInputProps = {
  label: string
  placeholder: string
  value: string | null
  disabled?: boolean
  error?: boolean
  inputId: string
  onTyped: (date: string | null) => void
}

function RangeInput({
  label,
  placeholder,
  value,
  disabled,
  error,
  inputId,
  onTyped,
}: RangeInputProps) {
  const formatted = value ? dayjs(value).format('MMM D, YYYY') : ''
  const [draft, setDraft] = useState(formatted)
  const [invalid, setInvalid] = useState(false)
  // Reflect external changes (calendar picks, filter resets)
  useEffect(() => {
    setDraft(formatted)
    setInvalid(false)
  }, [formatted])

  const commit = () => {
    if (draft.trim() === formatted.trim()) {
      setInvalid(false)
      return
    }
    const parsed = parseTypedDate(draft)
    if (parsed || draft.trim() === '') {
      onTyped(parsed)
      setInvalid(false)
    } else {
      // Unparseable: flag briefly and revert to the last good value
      setInvalid(true)
      setDraft(formatted)
    }
  }

  const borderClass =
    error || invalid ? 'border-themed-red-600' : 'border-themed-grey-200'
  const backgroundClass = disabled ? 'bg-themed-grey-100' : 'bg-white'
  const labelClass = disabled ? 'text-themed-grey-400' : 'text-themed-grey-500'
  const valueClass =
    disabled || !value ? 'text-themed-grey-400' : 'text-themed-grey-500'

  return (
    // A real <label> so a tap ANYWHERE on the field card focuses the input.
    // The bare <input> is only a ~17px strip (a third of the card): with a
    // mouse that's fine, but on touch most taps land on the label text or
    // the card padding and no keyboard ever appears — which read as "can't
    // type dates on mobile". Native label→input focus forwarding is the most
    // reliable tap-to-focus mechanism in iOS/Android webviews (no JS, no
    // synthetic-click quirks). select-text guards against a wrapper-injected
    // `user-select: none`, which blocks input focus on iOS.
    <label
      htmlFor={inputId}
      className={`relative block flex-1 box-border border ${borderClass} ${backgroundClass} rounded-lg p-2 min-w-0 select-text ${
        disabled ? '' : 'cursor-text'
      }`}
    >
      <Text variant="body1" className={`!text-[14px] ${labelClass}`}>
        {label}
      </Text>
      <input
        id={inputId}
        aria-label={label}
        className={`block w-full truncate pr-5 bg-transparent border-0 p-0 outline-none text-[14px] font-sans ${valueClass}`}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        enterKeyHint="done"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') (event.target as HTMLInputElement).blur()
        }}
      />
    </label>
  )
}

/**
 * Design System v2 Date Range Picker: paired start/end date inputs, each
 * opening a calendar, with range validation. Composed from the existing
 * component-library Datepicker.
 */
export default function DateRangePicker({
  startDate,
  endDate,
  onChange,
  disabled,
  id = 'date-range-picker',
}: DateRangePickerProps) {
  const hasInvalidRange = useMemo(
    () =>
      startDate !== null &&
      endDate !== null &&
      dayjs(endDate).isBefore(dayjs(startDate), 'day'),
    [startDate, endDate],
  )

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 w-full">
        {/* Relative wrappers anchor the library Datepicker's absolutely-positioned
            popover to each input; without one it positions against the page. */}
        <div className="relative flex-1 min-w-0">
          {/* The field itself is typeable (any year, no arrow-clicking);
              only the calendar icon opens the popover */}
          <RangeInput
            label="Start date"
            placeholder="Select start date"
            value={startDate}
            disabled={disabled}
            error={hasInvalidRange}
            inputId={`${id}-start-input`}
            onTyped={(date) => onChange(date, endDate)}
          />
          <Datepicker
            id={`${id}-start`}
            closeOnChange
            closeOnOutsideClick
            elevated
            initialValue={startDate}
            onChange={(date) => onChange(date, endDate)}
            // Re-read the field on every open: the calendar only reads
            // initialValue on mount, so a value set later (filter chip, typed
            // date) would otherwise open on the mount-time month
            resetOnOpen
            style={{
              transformOrigin: 'top left',
              left: 0,
              boxSizing: 'border-box',
            }}
          >
            <Datepicker.Activator>
              {/* top-0/right-0 + p-2 renders the icon at the same 8px inset
                  as the old top-2/right-2, but the padded box is a 32x32
                  touch target (the bare 16px icon was too small to tap) */}
              <div className="absolute top-0 right-0 p-2 cursor-pointer">
                <Icon
                  name="Calendar"
                  size={16}
                  color={disabled ? 'grey-400' : 'grey-500'}
                />
              </div>
            </Datepicker.Activator>
          </Datepicker>
        </div>
        <Text variant="body2" className="text-themed-grey-400 shrink-0">
          →
        </Text>
        <div className="relative flex-1 min-w-0">
          <RangeInput
            label="End date"
            placeholder="Select end date"
            value={endDate}
            disabled={disabled}
            error={hasInvalidRange}
            inputId={`${id}-end-input`}
            onTyped={(date) => onChange(startDate, date)}
          />
          <Datepicker
            id={`${id}-end`}
            closeOnChange
            closeOnOutsideClick
            elevated
            initialValue={endDate}
            onChange={(date) => onChange(startDate, date)}
            resetOnOpen
            style={{
              transformOrigin: 'top right',
              right: 0,
              boxSizing: 'border-box',
            }}
          >
            <Datepicker.Activator>
              {/* top-0/right-0 + p-2 renders the icon at the same 8px inset
                  as the old top-2/right-2, but the padded box is a 32x32
                  touch target (the bare 16px icon was too small to tap) */}
              <div className="absolute top-0 right-0 p-2 cursor-pointer">
                <Icon
                  name="Calendar"
                  size={16}
                  color={disabled ? 'grey-400' : 'grey-500'}
                />
              </div>
            </Datepicker.Activator>
          </Datepicker>
        </div>
      </div>
      {hasInvalidRange && (
        <Text variant="body4" className="!text-themed-red-600 mt-1">
          Please select a valid date range
        </Text>
      )}
    </div>
  )
}
